/*
 * The libraries' stylesheets come first, before anything of ours.
 *
 * Imports are evaluated in the order they are written, and a CSS module is
 * injected when the module importing it is evaluated - so with these at the
 * bottom every component's styles landed *above* Mantine's in the document,
 * and Mantine's resets won every tie on specificity. A class of ours setting
 * padding on an UnstyledButton, or display on a Drawer's content, silently
 * did nothing. Ours have to come last to win, which means theirs come first.
 */
import '@fontsource-variable/inter'
import '@mantine/core/styles.css'
import '@mantine/notifications/styles.css'
import '@mantine/tiptap/styles.css'
import '@mantine/charts/styles.css'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import './app.css'

import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { MantineProvider } from '@mantine/core'
import { Notifications } from '@mantine/notifications'
import { QueryClient } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { RouterProvider } from '@tanstack/react-router'
import { ApiError } from './api/client'
import { preloadEditor } from './components/RichTextField'
import { refreshWorker } from './push'
import { forgetEverything, persistOptions } from './query-persistence'
import { buildRouter } from './router'
import { warmStart } from './warm-start'
import { theme } from './theme'


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // An ERP is read constantly; refetching on every window focus turns a
      // day of tab-switching into thousands of requests.
      refetchOnWindowFocus: false,
      /**
       * Long enough that moving between screens reads from the cache rather
       * than refetching, which is what makes navigation feel instant rather
       * than merely fast. Anything that changes while you are looking at it
       * is invalidated by the mutation that changed it.
       */
      staleTime: 5 * 60_000,
      /**
       * Kept for a day, so yesterday's board is still there this morning and
       * paints before the refetch lands. The persisted copy has the same
       * lifetime.
       */
      gcTime: 24 * 60 * 60_000,
      retry: (failureCount, error) => {
        // 4xx means the request was wrong, and repeating it will not fix that.
        if (error instanceof ApiError && error.status < 500) return false
        return failureCount < 2
      },
    },
  },
})

/*
  A sign-in that arrived as a redirect rather than through the form.

  Google sends people back here rather than through the client's own sign-in
  path, so the cache it normally clears on sign-in would otherwise survive -
  and at a shared machine the first frame after signing in would be the last
  person's work. Cleared before the warm start rather than after, or the
  prefetch it fires would be thrown away with everything else.

  The flag is removed from the address bar immediately: it has been acted on,
  and leaving it there would clear the cache again on every refresh.
*/
if (new URLSearchParams(window.location.search).has('signed_in')) {
  forgetEverything(queryClient)

  const url = new URL(window.location.href)
  url.searchParams.delete('signed_in')
  window.history.replaceState({}, '', url)
}

/*
  Fired before the router mounts, so the page's own request is already in
  flight while the session is being checked rather than queued behind it.
*/
warmStart(queryClient)

/*
  The editor is the heaviest chunk and every task dialog needs it. Fetched
  while the first screen is idle, so opening a task never waits for it.
*/
preloadEditor()

/*
  Pull in a newer service worker if one has been deployed. Without this a
  browser keeps running the worker it registered whenever it first agreed to
  notifications, and a fix to notification behaviour never reaches it.
*/
void refreshWorker()

const router = buildRouter(queryClient)

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <MantineProvider theme={theme} defaultColorScheme="auto">
      {/*
        Restores the cache before the first render, so a refresh paints the
        board that was there rather than a spinner. See query-persistence.ts
        for what is deliberately left out of it.
      */}
      <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
        <Notifications position="top-right" />
        <RouterProvider router={router} />
      </PersistQueryClientProvider>
    </MantineProvider>
  </StrictMode>,
)
